import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { getGoogleApi } from 'selectors/featureToggles';
import { useSelector } from 'react-redux';
import noop from 'lodash/noop';
import { ProcessStatus } from '@xmatters/common/constants/commonStatus';
import {
  getEBGoogleMapsApiKey,
  isEBSEmbedded,
} from '@xmatters/common/context/ebsGlobalContext';
import { useConstantRefCallback } from '@xmatters/react';

const PRELOAD_LIBRARIRES = ['places', 'marker', 'drawing'];

const CONTEXT_DEFAULT = {
  googleMaps: undefined,
  init: noop,
};

export const GoogleMapsProviderContext = createContext(CONTEXT_DEFAULT);
export function GoogleMapsProvider({ children }) {
  const googleApiUrl = useSelector(getGoogleApi);
  const statusRef = useRef(ProcessStatus.NOT_LOADED);
  const init = useConstantRefCallback(() => {
    if (
      statusRef.current === ProcessStatus.LOADING ||
      statusRef.current === ProcessStatus.LOADED
    ) {
      return;
    }
    statusRef.current = ProcessStatus.LOADING;

    // Note, googleApiUrl includes `client` param, which is deprecated, and does not support some APIs
    // (see: https://developers.google.com/maps/premium/overview#client_id)
    // ex: https://maps.googleapis.com/maps/api/js?client=gme-xmattersinc
    // Ideally, we should be using `apiKey` param instead
    const auth = {};
    if (isEBSEmbedded()) {
      auth.apiKey = getEBGoogleMapsApiKey();
    } else if (googleApiUrl) {
      try {
        const url = new URL(googleApiUrl);
        auth.client = url.searchParams.get('client');
        auth.apiKey = url.searchParams.get('apiKey');
      } catch (err) {
        return;
      }
    } else {
      return;
    }
    const loader = new Loader({
      ...auth,
      version: 'quarterly',
    });

    loader
      .load()
      .then(async ({ maps }) => {
        await Promise.all(
          PRELOAD_LIBRARIRES.map(lib => maps.importLibrary(lib)),
        );
        statusRef.current = ProcessStatus.LOADED;
        // eslint-disable-next-line no-use-before-define
        setContext({
          init,
          googleMaps: maps,
        });
      })
      .catch(e => {
        statusRef.current = ProcessStatus.ERROR;
        throw e;
      });
  });

  const [context, setContext] = useState({ init, googleMaps: null });

  return (
    <GoogleMapsProviderContext.Provider value={context}>
      {children}
    </GoogleMapsProviderContext.Provider>
  );
}

export default function useGoogleMapsApi({ enabled } = { enabled: true }) {
  const { googleMaps, init } = useContext(GoogleMapsProviderContext);

  useEffect(() => {
    if (enabled) {
      init();
    }
  }, [init, enabled]);

  return enabled ? googleMaps : undefined;
}
